/**
 * Proveedor WebSocket para Yjs (protocolo compatible con y-websocket).
 * Implementación propia para controlar reconexión, estado de conexión y latido.
 */
import * as Y from 'yjs'
import * as awarenessProtocol from 'y-protocols/awareness'
import * as syncProtocol from 'y-protocols/sync'
import * as encoding from 'lib0/encoding'
import * as decoding from 'lib0/decoding'

const MESSAGE_SYNC = 0
/** Cada cuánto se manda el latido. */
const HEARTBEAT_MS = 15000
/** Sin noticias del servidor en este tiempo, la conexión se da por muerta. */
const HEARTBEAT_DEAD_MS = 40000
const MESSAGE_AWARENESS = 1
const MESSAGE_QUERY_AWARENESS = 3

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected'

export interface ProviderEvents {
  status: (status: ConnectionStatus) => void
  synced: (synced: boolean) => void
}

export class WebsocketProvider {
  readonly doc: Y.Doc
  readonly awareness: awarenessProtocol.Awareness
  private ws: WebSocket | null = null
  private url: string
  private shouldConnect = true
  private retries = 0
  private reconnectTimer: number | null = null
  private heartbeatTimer: number | null = null
  /** Última vez que llegó algo del servidor (el latido también cuenta). */
  private lastMessageAt = 0
  private listeners = new Map<keyof ProviderEvents, Set<(...args: never[]) => void>>()
  private _status: ConnectionStatus = 'connecting'
  private _synced = false

  constructor(url: string, doc: Y.Doc) {
    this.url = url
    this.doc = doc
    this.awareness = new awarenessProtocol.Awareness(doc)

    this.doc.on('update', this.handleDocUpdate)
    this.awareness.on('update', this.handleAwarenessUpdate)
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('beforeunload', this.destroyAwareness)
    document.addEventListener('visibilitychange', this.handleVisibility)

    this.connect()
  }

  get status(): ConnectionStatus {
    return this._status
  }

  get synced(): boolean {
    return this._synced
  }

  on<K extends keyof ProviderEvents>(event: K, cb: ProviderEvents[K]): () => void {
    let set = this.listeners.get(event)
    if (!set) {
      set = new Set()
      this.listeners.set(event, set)
    }
    set.add(cb as (...args: never[]) => void)
    return () => set!.delete(cb as (...args: never[]) => void)
  }

  private emit<K extends keyof ProviderEvents>(event: K, ...args: Parameters<ProviderEvents[K]>): void {
    for (const cb of this.listeners.get(event) ?? []) (cb as (...a: unknown[]) => void)(...args)
  }

  private setStatus(status: ConnectionStatus): void {
    if (this._status === status) return
    this._status = status
    this.emit('status', status)
  }

  private setSynced(value: boolean): void {
    if (this._synced === value) return
    this._synced = value
    this.emit('synced', value)
  }

  /**
   * Pide un ticket firmado por HTTP y lo añade a la URL. Hace falta porque el
   * WebView del iPad no siempre manda la cookie en el upgrade del WebSocket:
   * sin esto el tablero se ve pero no sincroniza.
   */
  private async ticketedUrl(): Promise<string> {
    const board = new URL(this.url).searchParams.get('board')
    if (!board) return this.url
    try {
      const res = await fetch(`/api/ws-ticket?board=${encodeURIComponent(board)}`, {
        credentials: 'same-origin',
      })
      if (!res.ok) return this.url
      const { ticket } = (await res.json()) as { ticket?: string }
      return ticket ? `${this.url}&ticket=${encodeURIComponent(ticket)}` : this.url
    } catch {
      return this.url
    }
  }

  private connect = (): void => {
    if (!this.shouldConnect || this.ws || this.connecting) return
    this.connecting = true
    this.setStatus('connecting')
    void this.ticketedUrl().then((url) => {
      this.connecting = false
      if (!this.shouldConnect || this.ws) return
      this.openSocket(url)
    })
  }

  private connecting = false

  private openSocket(url: string): void {
    let ws: WebSocket
    try {
      ws = new WebSocket(url)
    } catch {
      this.scheduleReconnect()
      return
    }
    ws.binaryType = 'arraybuffer'
    this.ws = ws

    ws.onopen = () => {
      this.retries = 0
      this.lastMessageAt = Date.now()
      this.setStatus('connected')
      // 1. paso 1 del protocolo de sincronización
      const encoder = encoding.createEncoder()
      encoding.writeVarUint(encoder, MESSAGE_SYNC)
      syncProtocol.writeSyncStep1(encoder, this.doc)
      this.send(encoding.toUint8Array(encoder))
      // 2. pedir el estado de presencia de los demás
      const aw = encoding.createEncoder()
      encoding.writeVarUint(aw, MESSAGE_QUERY_AWARENESS)
      this.send(encoding.toUint8Array(aw))
      // 3. publicar el propio
      if (this.awareness.getLocalState() !== null) {
        const enc = encoding.createEncoder()
        encoding.writeVarUint(enc, MESSAGE_AWARENESS)
        encoding.writeVarUint8Array(
          enc,
          awarenessProtocol.encodeAwarenessUpdate(this.awareness, [this.doc.clientID]),
        )
        this.send(encoding.toUint8Array(enc))
      }
      this.startHeartbeat()
    }

    ws.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      this.lastMessageAt = Date.now()
      const data = new Uint8Array(event.data)
      if (data.length === 0) return
      const decoder = decoding.createDecoder(data)
      const encoder = encoding.createEncoder()
      const type = decoding.readVarUint(decoder)
      switch (type) {
        case MESSAGE_SYNC: {
          encoding.writeVarUint(encoder, MESSAGE_SYNC)
          const syncType = syncProtocol.readSyncMessage(decoder, encoder, this.doc, this)
          if (syncType === syncProtocol.messageYjsSyncStep2) this.setSynced(true)
          if (encoding.length(encoder) > 1) this.send(encoding.toUint8Array(encoder))
          break
        }
        case MESSAGE_AWARENESS: {
          awarenessProtocol.applyAwarenessUpdate(this.awareness, decoding.readVarUint8Array(decoder), this)
          break
        }
        case MESSAGE_QUERY_AWARENESS: {
          const enc = encoding.createEncoder()
          encoding.writeVarUint(enc, MESSAGE_AWARENESS)
          encoding.writeVarUint8Array(
            enc,
            awarenessProtocol.encodeAwarenessUpdate(this.awareness, Array.from(this.awareness.getStates().keys())),
          )
          this.send(encoding.toUint8Array(enc))
          break
        }
        default:
          break
      }
    }

    ws.onclose = () => {
      this.ws = null
      this.stopHeartbeat()
      this.setSynced(false)
      awarenessProtocol.removeAwarenessStates(
        this.awareness,
        Array.from(this.awareness.getStates().keys()).filter((id) => id !== this.doc.clientID),
        this,
      )
      this.setStatus('disconnected')
      this.scheduleReconnect()
    }

    ws.onerror = () => {
      try {
        ws.close()
      } catch {
        /* noop */
      }
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldConnect || this.reconnectTimer !== null) return
    const delay = Math.min(1000 * Math.pow(1.5, this.retries++), 10000)
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()
    // Un ping vacío periódico evita que proxies intermedios corten la conexión,
    // y el servidor lo contesta: si pasa mucho sin oír nada, la conexión está
    // muerta aunque diga OPEN (iPad que se suspendió, cambio de Wi-Fi, LTE) y
    // se cierra para reconectar. Antes el punto seguía verde sin sincronizar.
    this.heartbeatTimer = window.setInterval(() => {
      if (this.ws?.readyState !== WebSocket.OPEN) return
      if (Date.now() - this.lastMessageAt > HEARTBEAT_DEAD_MS) {
        this.ws.close()
        return
      }
      this.ws.send(new Uint8Array(0))
    }, HEARTBEAT_MS)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private handleOnline = (): void => {
    if (!this.ws) {
      this.retries = 0
      this.connect()
    }
  }

  private handleVisibility = (): void => {
    if (document.visibilityState !== 'visible') return
    if (!this.ws) {
      this.retries = 0
      this.connect()
      return
    }
    // Al volver de segundo plano (iPad que se durmió): si hace rato que no se
    // oye al servidor, la conexión probablemente murió en silencio; se cierra
    // y onclose reconecta enseguida. Si vive, el latido la mantiene.
    if (Date.now() - this.lastMessageAt > HEARTBEAT_MS + 5000) {
      this.retries = 0
      try {
        this.ws.close()
      } catch {
        /* noop */
      }
    } else if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(new Uint8Array(0))
    }
  }

  private handleDocUpdate = (update: Uint8Array, origin: unknown): void => {
    if (origin === this) return
    const encoder = encoding.createEncoder()
    encoding.writeVarUint(encoder, MESSAGE_SYNC)
    syncProtocol.writeUpdate(encoder, update)
    this.send(encoding.toUint8Array(encoder))
  }

  private handleAwarenessUpdate = (
    { added, updated, removed }: { added: number[]; updated: number[]; removed: number[] },
    origin: unknown,
  ): void => {
    if (origin === this) return
    const changed = added.concat(updated, removed)
    const encoder = encoding.createEncoder()
    encoding.writeVarUint(encoder, MESSAGE_AWARENESS)
    encoding.writeVarUint8Array(encoder, awarenessProtocol.encodeAwarenessUpdate(this.awareness, changed))
    this.send(encoding.toUint8Array(encoder))
  }

  private destroyAwareness = (): void => {
    awarenessProtocol.removeAwarenessStates(this.awareness, [this.doc.clientID], 'window unload')
  }

  private send(data: Uint8Array): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(data)
      } catch {
        this.ws.close()
      }
    }
  }

  destroy(): void {
    this.shouldConnect = false
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.stopHeartbeat()
    this.destroyAwareness()
    this.doc.off('update', this.handleDocUpdate)
    this.awareness.off('update', this.handleAwarenessUpdate)
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('beforeunload', this.destroyAwareness)
    document.removeEventListener('visibilitychange', this.handleVisibility)
    this.ws?.close()
    this.ws = null
    this.awareness.destroy()
  }
}
